import { skipToken, useMutation } from '@apollo/client'
import { Check, ChevronsUpDown, Loader, X } from 'lucide-react'
import { Suspense, useState, useTransition } from 'react'
import { toast } from 'sonner'
import { ToasterContent } from '@/components/sv/toaster-content'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { gql } from '@/lib/graphql/gql'
import { useIdCompany, useSuspenseQuery } from '@/lib/hooks'
import { cn } from '@/lib/utils'
import { AddExceptionSheetFallback } from '@/routes/single-sign-on/exceptions/add-exception-sheet/fallback'
import type {
  AddSsoExceptionQuery,
  AddSsoExceptionQueryVariables,
  AddUsersToSamlExceptionsMutation,
  AddUsersToSamlExceptionsMutationVariables,
} from '@/routes/single-sign-on/exceptions/add-exception-sheet/index.types.gen'

const ADD_EXCEPTION_QUERY = gql`
  query AddSsoException($idCompany: String!) {
    company(id: $idCompany) {
      id
      users {
        id
        capabilities @live {
          id
          isSamlExempt
        }
        user {
          id
          email
          firstName
          lastName
        }
      }
    }
  }
`

const ADD_USERS_TO_SAML_EXCEPTIONS_MUTATION = gql`
  mutation AddUsersToSamlExceptions($idUsers: [String!]!, $idCompany: String!) {
    addUsersToSamlExceptions(idUsers: $idUsers, idCompany: $idCompany) {
      id
      isSamlExempt
    }
  }
`

type Props = {
  isOpen: boolean
  onClose: () => void
}

const getInitials = (firstName: string, lastName: string) => {
  return `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.toUpperCase()
}

const Content = ({ isOpen, onClose }: Props) => {
  const { idCompany } = useIdCompany()
  const [isPending, startTransition] = useTransition()
  const [selectedUsers, setSelectedUsers] = useState<string[]>([])
  const [isUserSelectOpen, setIsUserSelectOpen] = useState(false)

  const { data } = useSuspenseQuery<
    AddSsoExceptionQuery,
    AddSsoExceptionQueryVariables
  >(ADD_EXCEPTION_QUERY, isOpen ? { variables: { idCompany } } : skipToken)

  const [addUsersToExceptions] = useMutation<
    AddUsersToSamlExceptionsMutation,
    AddUsersToSamlExceptionsMutationVariables
  >(ADD_USERS_TO_SAML_EXCEPTIONS_MUTATION)

  const users = data?.company?.users ?? []

  const nonExceptionUsers = users
    .filter((cu) => !cu.capabilities?.some((c) => c.isSamlExempt))
    .map((cu) => ({
      id: cu.id,
      idUser: cu.user?.id ?? '',
      email: cu.user?.email ?? '',
      firstName: cu.user?.firstName ?? '',
      lastName: cu.user?.lastName ?? '',
    }))

  const toggleUserSelection = (idUser: string) => {
    setSelectedUsers((prev) =>
      prev.includes(idUser)
        ? prev.filter((id) => id !== idUser)
        : [...prev, idUser]
    )
  }

  const onSubmit = async () => {
    if (selectedUsers.length === 0) {
      return
    }

    startTransition(async () => {
      try {
        await addUsersToExceptions({
          variables: { idUsers: selectedUsers, idCompany },
        })
        toast(
          <ToasterContent variant="success">
            Exceptions added successfully
          </ToasterContent>,
          { position: 'top-right' }
        )
        setSelectedUsers([])
        onClose()
      } catch {
        toast(
          <ToasterContent variant="error">Failed to exceptions</ToasterContent>,
          { position: 'top-right' }
        )
      }
    })
  }

  if (nonExceptionUsers?.length === 0) {
    return (
      <>
        <SheetHeader>
          <SheetTitle>Add exceptions</SheetTitle>
          <SheetDescription>
            Select users to add to the exceptions list. They will be able to
            sign in with a password even when SSO is mandatory.
          </SheetDescription>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto px-4 space-y-4">
          <p className="text-sm text-muted-foreground text-center py-4">
            All users are already on the exceptions list.
          </p>
        </div>
        <SheetFooter className="sticky">
          <Button disabled>Add</Button>
        </SheetFooter>
      </>
    )
  }

  return (
    <>
      <SheetHeader>
        <SheetTitle>Add exceptions</SheetTitle>
        <SheetDescription>
          Select users to add to the exceptions list. They will be able to sign
          in with a password even when SSO is mandatory.
        </SheetDescription>
      </SheetHeader>
      <div className="flex-1 overflow-y-auto px-4 space-y-4">
        <Popover
          open={isUserSelectOpen}
          onOpenChange={setIsUserSelectOpen}
          modal
        >
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={isUserSelectOpen}
              className="w-full justify-between"
            >
              Select users...
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[var(--radix-popper-anchor-width)] p-0">
            <Command>
              <CommandInput placeholder="Search users..." />
              <CommandList>
                <CommandEmpty>No users found.</CommandEmpty>
                <CommandGroup>
                  {nonExceptionUsers.map((user) => {
                    const isSelected = selectedUsers.includes(user.idUser)
                    return (
                      <CommandItem
                        key={user.id}
                        value={`${user.firstName} ${user.lastName} ${user.email}`}
                        onSelect={() => {
                          toggleUserSelection(user.idUser)
                        }}
                        className="cursor-pointer"
                      >
                        <Avatar className="h-6 w-6 mr-2">
                          <AvatarFallback className="text-xs">
                            {getInitials(user.firstName, user.lastName)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">
                            {user.firstName} {user.lastName}
                          </p>
                          <p className="text-xs text-muted-foreground truncate">
                            {user.email}
                          </p>
                        </div>
                        <Check
                          className={cn(
                            'ml-auto h-4 w-4',
                            isSelected ? 'opacity-100' : 'opacity-0'
                          )}
                        />
                      </CommandItem>
                    )
                  })}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        {selectedUsers.length > 0 && (
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              Selected users ({selectedUsers.length})
            </p>
            <div className="space-y-2">
              {selectedUsers?.map((idUser) => {
                const user = nonExceptionUsers?.find((u) => u.idUser === idUser)
                if (!user) {
                  return null
                }
                return (
                  <div
                    key={idUser}
                    className="flex items-center justify-between p-3 rounded-lg border bg-muted/30"
                  >
                    <div className="flex items-center gap-3">
                      <Avatar className="h-8 w-8">
                        <AvatarFallback className="text-xs">
                          {getInitials(user?.firstName, user?.lastName)}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="text-sm font-medium">
                          {user.firstName} {user.lastName}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {user.email}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Remove ${user.firstName} ${user.lastName} from selection`}
                      onClick={() => toggleUserSelection(idUser)}
                      className="text-muted-foreground hover:text-destructive"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </div>
      <SheetFooter className="sticky">
        <Button disabled={isPending} onClick={() => onSubmit()}>
          {isPending ? <Loader className="animate-spin" /> : null}
          Add {selectedUsers.length > 0 ? `(${selectedUsers.length})` : ''}
        </Button>
      </SheetFooter>
    </>
  )
}

export const AddExceptionSheet = ({ isOpen, onClose }: Props) => {
  return (
    <Sheet open={isOpen} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <SheetContent className="sm:max-w-[600px]" onEscapeKeyDown={onClose}>
        <Suspense fallback={<AddExceptionSheetFallback />}>
          <Content isOpen={isOpen} onClose={onClose} />
        </Suspense>
      </SheetContent>
    </Sheet>
  )
}
